import { type DocNode } from '@atlaskit/adf-schema';

/**
 * Extracts plain text content from an ADF (Atlassian Document Format) document node.
 * Recursively traverses the node tree and collects all text content.
 *
 * Handles the following non-text node types that Confluence's editor automatically
 * converts user input into:
 *   - `inlineCard` / `blockCard` / `embedCard`: URLs converted to smart links → extracts `attrs.url`
 *   - `inlineExtension` (Rovo skill/action tags, e.g. `/get-project-details`) → extracts the
 *     human-readable skill name from `attrs.parameters.name`, falling back to `attrs.parameters.slug`
 *   - `mention`: @-mentions → extracts `attrs.text`
 *
 * Copied from conversation-assistant/src/common/utils/content-features to avoid
 * a cross-package internal import dependency.
 */
export function extractPlainTextFromAdf(doc: DocNode): string {
	if (!doc || !doc.content) {
		return '';
	}

	return extractFromNodes(doc.content as AdfNode[]).trim();
}

type AdfNode = {
	type?: string;
	text?: string;
	attrs?: Record<string, unknown> & {
		url?: string;
		text?: string;
		parameters?: Record<string, unknown>;
	};
	content?: AdfNode[];
};
function extractFromNodes(nodes: AdfNode[]): string {
	if (!Array.isArray(nodes)) {
		return '';
	}

	return nodes
		.map((node) => {
			if (node.type === 'text') {
				return node.text || '';
			}
			// Smart links: URLs auto-converted by the editor to inline/block/embed cards
			if (node.type === 'inlineCard' || node.type === 'blockCard' || node.type === 'embedCard') {
				return node.attrs?.url || '';
			}
			// Rovo skill/action tags: e.g. /get-project-details typed as a slash command
			if (node.type === 'inlineExtension') {
				const skillName =
					(node.attrs?.parameters?.slug as string | undefined) ||
					(node.attrs?.parameters?.name as string | undefined);
				return skillName ? `/${skillName}` : '';
			}
			// @-mentions
			if (node.type === 'mention') {
				return node.attrs?.text || '';
			}
			if (node.content) {
				const childText = extractFromNodes(node.content);
				// Add newline after block-level nodes
				if (
					node.type &&
					[
						'paragraph',
						'heading',
						'bulletList',
						'orderedList',
						'listItem',
						'blockquote',
						'codeBlock',
					].includes(node.type)
				) {
					return childText + '\n';
				}
				return childText;
			}
			if (node.type === 'hardBreak') {
				return '\n';
			}
			return '';
		})
		.join('');
}
